import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Button from '@mui/material/Button';
import { TABS } from '@/pages/decedent-profile/constants';
import styles from '@/pages/decedent-profile/styles.less';
import {
  DecedentDependants,
  DecedentMainInfo,
  DecedentMarital,
  DecedentResidence,
  DecedentDocs,
} from '@/common/containers/Forms/index';
import { PromptSaveModal } from '@/common/containers/Modals/index';
import { useTranslation } from 'react-i18next';
import { useCurrentDecedent } from '@/app/providers/DecedentProvider';
import {
  useEditDecedentMutation,
  useSubmitQuestionMutation,
} from '@/entities/decedent';

const FORM_ID = 'decedentProfile';

const Form = ({ tab }) => {
  const { t } = useTranslation();
  const decedent = useCurrentDecedent();
  const editDecedentMutation = useEditDecedentMutation();
  const submitQuestionMutation = useSubmitQuestionMutation();
  const [formState, setFormState] = useState({});
  const submitRef = useRef(() => {});
  const isSubmitting =
    tab === TABS.MAIN || tab === TABS.RESIDENCE
      ? editDecedentMutation.isPending
      : submitQuestionMutation.isPending;

  useEffect(() => {
    window.onbeforeunload = formState.isDirty ? () => true : null;
    return () => {
      window.onbeforeunload = null;
    };
  }, [formState.isDirty]);

  const onSubmit = useCallback(
    async (form) => {
      const getAction = () => {
        switch (tab) {
          case TABS.MAIN:
          case TABS.RESIDENCE: {
            return editDecedentMutation.mutateAsync;
          }
          case TABS.MARITAL:
          case TABS.DEPENDANTS:
          case TABS.DOCS: {
            return ({ id, form }) =>
              submitQuestionMutation.mutateAsync({
                decedentId: id,
                answers: form,
                section: `basic_${tab}`,
              });
          }
        }
      };
      const action = getAction();

      await action({ id: decedent.id, form });
      return 'success';
    },
    [decedent?.id, editDecedentMutation, submitQuestionMutation, tab]
  );

  const Form = useMemo(() => {
    const props = {
      formId: FORM_ID,
      onSubmit,
      onFormStateChange: (form, submitForm) => {
        setFormState(form);
        submitRef.current = submitForm;
      },
      validationMode: 'onChange',
    };
    if ([TABS.MAIN, TABS.RESIDENCE].includes(tab))
      props.initialValues = decedent;
    const forms = {
      [TABS.MAIN]: DecedentMainInfo,
      [TABS.RESIDENCE]: DecedentResidence,
      [TABS.MARITAL]: DecedentMarital,
      [TABS.DEPENDANTS]: DecedentDependants,
      [TABS.DOCS]: DecedentDocs,
    };
    const Component = forms[tab];
    return Component ? React.cloneElement(<Component />, props) : null;
  }, [tab, decedent, onSubmit]);

  const saveProgress = useCallback(async () => {
    if (!formState.isValid) {
      submitRef.current();
      throw new Error(t('form not valid'));
    }
    await submitRef.current();
  }, [formState.isValid, t]);

  return (
    <div className={styles.form}>
      <PromptSaveModal when={!!formState.isDirty} onSave={saveProgress} />
      <div className={styles.formContent}>
        <div className={styles.formWrapper}>{Form}</div>
      </div>
      <div className={styles.formBtns}>
        <Button
          type="submit"
          form={FORM_ID}
          variant="contained"
          disabled={!formState.isDirty}
          loading={isSubmitting}
        >
          {t('Save Updates')}
        </Button>
      </div>
    </div>
  );
};

export default Form;
